import React from 'react';
import { Lightning, Microscope, Flag, Eye, Pencil } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

export function AlertLogSection({ alertLog, emailConfig }) {
  return (
    <div className="card-flat overflow-hidden mt-8">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#111827]">Email Alerts</h2>
          <p className="text-sm text-[#4B5563]">Admin notifications for safety flags</p>
        </div>
        {emailConfig && (
          <div className="flex items-center gap-2">
            {emailConfig.sendgrid_configured ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-sm status-acceptable" data-testid="email-status-active">Active</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-sm status-needs-review" data-testid="email-status-pending">Key Required</span>
            )}
          </div>
        )}
      </div>

      {emailConfig && !emailConfig.sendgrid_configured && (
        <div className="p-4 bg-[#FDF6B2] border-b border-[#FACA15]">
          <p className="text-sm text-[#723B13]">
            <strong>SendGrid API key not configured.</strong> Alerts are being logged but not emailed.
            Add your SendGrid API key to the SENDGRID_API_KEY environment variable to enable email delivery.
          </p>
        </div>
      )}

      {emailConfig && (
        <div className="p-4 border-b border-gray-200 bg-[#F9FAFB]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">Alert Triggers</p>
          <div className="flex flex-wrap gap-2">
            {emailConfig.alert_triggers?.map((trigger) => (
              <span key={trigger} className="text-xs px-2 py-1 bg-white border border-gray-200 rounded-sm text-[#111827]">{trigger}</span>
            ))}
          </div>
          <p className="text-xs text-[#4B5563] mt-2">Sender: <span className="font-mono">{emailConfig.sender_email}</span></p>
        </div>
      )}

      {alertLog.length === 0 ? (
        <div className="p-8 text-center"><p className="text-[#4B5563] text-sm">No alerts triggered yet</p></div>
      ) : (
        <div className="divide-y divide-gray-200">
          {alertLog.slice(0, 10).map((alert) => (
            <div key={alert.formulation_id + alert.sent_at} className="p-4 hover:bg-gray-50" data-testid={`alert-log-row-${alert.formulation_id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#111827] text-sm">{alert.product_name}</h3>
                <div className="flex items-center gap-2">
                  {alert.email_sent ? (
                    <span className="text-xs font-medium text-[#0E9F6E] bg-[#DEF7EC] px-2 py-0.5 rounded-sm">Emailed</span>
                  ) : (
                    <span className="text-xs font-medium text-[#723B13] bg-[#FDF6B2] px-2 py-0.5 rounded-sm">Logged Only</span>
                  )}
                  <span className="text-xs text-[#4B5563] tabular-nums">{new Date(alert.sent_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {alert.alerts?.map((a) => (
                  <span key={a.ingredient + a.flag} className={`text-xs px-2 py-0.5 rounded-sm font-medium ${a.severity === 'high' ? 'bg-[#FDE8E8] text-[#9B1C1C]' : 'bg-[#FDF6B2] text-[#723B13]'}`}>
                    {a.ingredient}: {a.flag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FormulationRow({ formulation, onOverride, onToggleFlag }) {
  return (
    <div className="p-4 hover:bg-gray-50 transition-colors" data-testid={`admin-formulation-row-${formulation.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-semibold text-[#111827] truncate">{formulation.metadata?.product_name || 'Untitled'}</h3>
            {formulation.flagged && <Flag size={16} className="text-[#F05252] flex-shrink-0" weight="fill" />}
            {formulation.has_override && (
              <span className="text-xs font-medium text-[#2563EB] bg-[#E1EFFE] px-2 py-0.5 rounded-sm flex-shrink-0">Override</span>
            )}
            {formulation.assessment_mode === 'hazard' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] bg-[#E1EFFE] rounded-sm flex-shrink-0">
                <Lightning size={10} weight="bold" /> Risk Screen
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white bg-[#0B2447] rounded-sm flex-shrink-0">
                <Microscope size={10} weight="bold" /> Full QRA
              </span>
            )}
          </div>
          <p className="text-sm text-[#4B5563]">
            <span className="font-medium">{formulation.user_info?.name || '—'}</span>
            <span className="mx-1">·</span>{formulation.user_info?.email || '—'}
            {formulation.user_info?.company && <><span className="mx-1">·</span>{formulation.user_info.company}</>}
            <span className="mx-2">·</span>
            <span className="tabular-nums">{new Date(formulation.created_at).toLocaleDateString()}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 ml-4">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-sm ${
            formulation.overall_status === 'acceptable' ? 'status-acceptable' :
            formulation.overall_status === 'not_acceptable' ? 'status-not-acceptable' : 'status-needs-review'
          }`}>
            {(formulation.overall_status || 'needs_review').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
          <div className="flex gap-2">
            <Link to={`/assessment/${formulation.id}`}>
              <Button variant="outline" size="sm" className="rounded-sm h-9" data-testid={`view-btn-${formulation.id}`}>
                <Eye size={16} className="mr-1" /> View
              </Button>
            </Link>
            <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => onOverride(formulation)} data-testid={`override-btn-${formulation.id}`}>
              <Pencil size={16} className="mr-1" /> Override
            </Button>
            <Button variant={formulation.flagged ? "default" : "outline"} size="sm"
              className={`rounded-sm h-9 ${formulation.flagged ? 'bg-[#F05252] hover:bg-[#DC2626]' : ''}`}
              onClick={() => onToggleFlag(formulation.id)} data-testid={`flag-btn-${formulation.id}`}>
              <Flag size={16} weight={formulation.flagged ? "fill" : "regular"} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
